/**
 * Getting your data out, and your calendar in sync.
 *
 * The app could import spreadsheets and never export anything — forty years of
 * records with no way to take them anywhere.
 */
import { useQuery } from '@tanstack/react-query';
import { can } from '@gwc/shared';
import { api, apiUrl } from '../lib/api';
import { useAuth } from '../auth/AuthProvider';
import { toast } from '../lib/toast';
import { Button } from './ui';

/**
 * CSV endpoints are auth'd, so a bare <a download> won't carry the session in
 * every browser. Fetch it, then hand the blob to the browser.
 */
async function download(path: string, filename: string) {
  try {
    const blob = await api.blob(path);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  } catch {
    toast.error('That export could not be generated.');
  }
}

export function ExportPanel() {
  const { user } = useAuth();

  // The calendar feed is token-based: a calendar client can't log in, it just
  // fetches a URL forever.
  const feed = useQuery({
    queryKey: ['calendar-token'],
    queryFn: () => api.get<{ data: { url: string } }>('/export/calendar-token').then((r) => r.data),
  });

  if (!user) return null;

  return (
    <section className="card space-y-5 p-6">
      <div>
        <h2 className="font-display text-xl text-cream-50">Your data</h2>
        <p className="mt-1 text-sm text-cream-300/70">
          Take the club's records with you, and keep the tasting calendar in sync.
        </p>
      </div>

      <div>
        <h3 className="mb-2 text-xs uppercase tracking-wide text-cream-300/45">Export</h3>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="ghost"
            onClick={() => download('/export/archive.csv', 'gwc-archive.csv')}
          >
            Archive (CSV)
          </Button>
          {can(user.roles, 'users:manage') ? (
            <Button
              variant="ghost"
              onClick={() => download('/export/members.csv', 'gwc-members.csv')}
            >
              Members (CSV)
            </Button>
          ) : null}
          {can(user.roles, 'payments:manage') ? (
            <Button variant="ghost" onClick={() => download('/export/dues.csv', 'gwc-dues.csv')}>
              Dues ledger (CSV)
            </Button>
          ) : null}
        </div>
      </div>

      <div>
        <h3 className="mb-2 text-xs uppercase tracking-wide text-cream-300/45">
          Subscribe to the tasting calendar
        </h3>
        <p className="mb-2 text-sm text-cream-300/60">
          Paste this into Apple Calendar or Google Calendar and every tasting appears
          automatically — and updates when a date changes.
        </p>
        <div className="flex gap-2">
          <input
            readOnly
            className="field flex-1 text-xs"
            value={feed.data?.url ?? 'Loading…'}
            onFocus={(e) => e.currentTarget.select()}
          />
          <Button
            variant="ghost"
            onClick={() => {
              if (!feed.data?.url) return;
              void navigator.clipboard.writeText(feed.data.url);
              toast.success('Calendar link copied.');
            }}
          >
            Copy
          </Button>
        </div>
      </div>
    </section>
  );
}

export { apiUrl };
